import { Info } from "lucide-react";
import { Link } from "react-router-dom";
import { ContinueCard } from "@/components/skills/ContinueCard";
import { MiniMap } from "@/components/skills/MiniMap";
import { StartCard } from "@/components/skills/StartCard";
import { useT } from "@/lib/i18n";
import { countMastered, useScenarios } from "@/lib/scenarios";

/**
 * Home body for SCENARIOS mode. Restructured for the new pedagogy:
 *
 *   1. Big hero card — either `ContinueCard` (in-flight scenario) or
 *      `StartCard` ("Start here →" pointing at the first AVAILABLE
 *      scenario for fresh users). One of these is always present
 *      under the new lock model (foundational scenarios always
 *      AVAILABLE).
 *   2. `MiniMap` — sequential preview of ~5 nearby scenarios so the
 *      learner can see where they are in the chain without leaving
 *      Home. Tap any non-locked row to jump; "open full map" link
 *      below goes to /skills/tree.
 *   3. `CapabilityHero` — the "Я могу N из 60 умений" mastered counter,
 *      pushed below the actionable surfaces. Useful long-term, but
 *      not motivating when N=0, so it doesn't dominate the empty
 *      state.
 *   4. `ModeFootnote` — the "what is Skills mode?" hint.
 *
 * Replaces the previous "next-up preview + dashed empty state"
 * pattern, which felt anaemic on day one.
 */
export function HomeSkillsBody() {
  const t = useT();
  const scenarios = useScenarios();

  const all = scenarios.data ?? [];
  const mastered = countMastered(all);
  const total = all.length;

  // Continue priority: READY_FOR_CHECKPOINT first (higher-stakes
  // moment), then IN_PROGRESS. Drives the hero card.
  const ready = all.find((s) => s.progress?.status === "READY_FOR_CHECKPOINT");
  const inProgress = all.find((s) => s.progress?.status === "IN_PROGRESS");
  const current = ready ?? inProgress;

  // Fresh-user start scenario — first AVAILABLE in stage order. With
  // the new lock model the foundational scenario is always AVAILABLE
  // from day one, so this is non-null in 99.9% of cases.
  const firstAvailable = current
    ? undefined
    : all
        .filter((s) => s.progress?.status === "AVAILABLE")
        .toSorted((a, b) => a.stage - b.stage || a.slug.localeCompare(b.slug))[0];

  return (
    <>
      {current ? (
        <ContinueCard scenario={current} />
      ) : firstAvailable ? (
        <StartCard scenario={firstAvailable} />
      ) : (
        // Truly empty — seed data has no AVAILABLE scenarios at all.
        // Shouldn't happen with the new lock model, but defensive.
        <Link
          to="/skills"
          className="rounded-[var(--r-md)] border border-dashed border-border bg-surface px-4 py-5 text-center text-[13px] text-text-3 transition-colors hover:bg-[color:var(--surface-2)]"
        >
          {t("home_skills.empty_journal")}
        </Link>
      )}

      {all.length > 0 && <MiniMap scenarios={all} />}

      <CapabilityHero mastered={mastered} total={total} />

      <ModeFootnote />
    </>
  );
}

function CapabilityHero({ mastered, total }: { mastered: number; total: number }) {
  const t = useT();
  // Tick bar grouped 6×10 — matches the journal hero so the user sees
  // the same progress visual on Home and in the journal. Same
  // rationale as `Skills.tsx CapabilityHero`.
  const TICK_GROUPS = 6;
  const TICKS_PER_GROUP = 10;
  return (
    <div
      className="rounded-[var(--r-lg)] p-[18px] shadow-[var(--shadow-sm)]"
      style={{
        background: "linear-gradient(140deg, var(--scen-bg2) 0%, var(--surface) 75%)",
      }}
    >
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: "var(--scen-fg)" }}>
        {t("skills.hero_label")}
      </div>
      <div className="mb-3.5 flex items-baseline gap-2">
        <span className="text-[44px] font-bold leading-none tracking-[-0.04em]" style={{ color: "var(--scen-ink)" }}>
          {mastered}
        </span>
        <span className="text-[14px] text-text-3">{t("skills.hero_count_of", { total: String(total) })}</span>
      </div>
      <div className="flex gap-1.5">
        {Array.from({ length: TICK_GROUPS }).map((_, gi) => (
          <div key={gi} className="flex flex-1 gap-[2px]">
            {Array.from({ length: TICKS_PER_GROUP }).map((__, ti) => {
              const i = gi * TICKS_PER_GROUP + ti;
              const filled = i < mastered;
              const upcoming = i >= mastered && i < Math.min(mastered + 3, total);
              return (
                <div
                  key={ti}
                  className="h-1.5 flex-1 rounded-[1.5px]"
                  style={{
                    background: filled ? "var(--scen-solid)" : upcoming ? "var(--scen-bg2)" : "var(--surface-3)",
                    visibility: i < TICK_GROUPS * TICKS_PER_GROUP ? undefined : "hidden",
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function ModeFootnote() {
  const t = useT();
  return (
    <div className="flex items-start gap-2.5 rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-3.5 py-3 text-[12px] text-text-2">
      <Info className="mt-0.5 size-4 shrink-0" style={{ color: "var(--scen-fg)" }} />
      <div className="flex-1">
        <b className="text-text">{t("home_skills.mode_hint_title")}</b> — {t("home_skills.mode_hint_body")}
      </div>
    </div>
  );
}
